import type { MiddlewareFunction } from '@segment/analytics-next';

import { analytics } from 'segmentAnalytics';

// Cookies are stored at .themartec.com so the session is shared across
// app.themartec.com (web-fe) and app-next.themartec.com (web-next-gen).
// The cookie's Max-Age implements the 30-min sliding-window idle expiry —
// each ensureSession() call rewrites the cookies, resetting the TTL.
export const SESSION_TIMEOUT_MS = 30 * 60 * 1000;
const COOKIE_MAX_AGE_SECONDS = SESSION_TIMEOUT_MS / 1000;

const SESSION_ID_KEY = 'segment_session_id';
const SESSION_USER_ID_KEY = 'segment_session_user_id';

export interface SessionContext {
  userId: string;
  companyId: string;
  persona: string;
}

const generateSessionId = (): string => {
  if (
    typeof crypto !== 'undefined' &&
    typeof (crypto as any).randomUUID === 'function'
  ) {
    return (crypto as any).randomUUID();
  }
  if (
    typeof crypto !== 'undefined' &&
    typeof crypto.getRandomValues === 'function'
  ) {
    const buf = new Uint8Array(16);
    crypto.getRandomValues(buf);
    buf[6] = (buf[6] & 0x0f) | 0x40;
    buf[8] = (buf[8] & 0x3f) | 0x80;
    const hex = Array.from(buf, (b) => b.toString(16).padStart(2, '0'));
    return `${hex.slice(0, 4).join('')}-${hex.slice(4, 6).join('')}-${hex
      .slice(6, 8)
      .join('')}-${hex.slice(8, 10).join('')}-${hex.slice(10, 16).join('')}`;
  }
  return `${Date.now()}-${Date.now().toString(36)}`;
};

const getCookieDomain = (): string => {
  if (typeof window === 'undefined') return '';
  const { hostname } = window.location;
  // Share across *.themartec.com. On localhost or other dev hostnames,
  // return '' — cookies stay port-scoped (dev limitation, accept on staging).
  if (hostname.endsWith('.themartec.com')) return '.themartec.com';
  return '';
};

const readCookie = (name: string): string | null => {
  if (typeof document === 'undefined') return null;
  try {
    const cookies = `; ${document.cookie}`;
    const parts = cookies.split(`; ${name}=`);
    if (parts.length !== 2) return null;
    const tail = parts.pop();
    if (!tail) return null;
    const raw = tail.split(';').shift();
    return raw ? decodeURIComponent(raw) : null;
  } catch {
    return null;
  }
};

const writeCookie = (name: string, value: string): void => {
  if (typeof document === 'undefined') return;
  try {
    const domain = getCookieDomain();
    const isSecure =
      typeof window !== 'undefined' && window.location.protocol === 'https:';
    const segments = [
      `${name}=${encodeURIComponent(value)}`,
      `Max-Age=${COOKIE_MAX_AGE_SECONDS}`,
      'Path=/',
      'SameSite=Lax',
    ];
    if (domain) segments.push(`Domain=${domain}`);
    if (isSecure) segments.push('Secure');
    document.cookie = segments.join('; ');
  } catch {
    // ignore — best-effort
  }
};

const fireSessionStarted = (
  sessionId: string,
  startedAt: number,
  context: SessionContext,
): void => {
  analytics.track(
    'session.started',
    {
      userId: context.userId,
      companyId: context.companyId,
      timestamp: new Date(startedAt).toISOString(),
      persona: context.persona,
      event_class: 'passive',
      session_id: sessionId,
    },
    { context: { groupId: context.companyId } },
  );
};

export const getSessionId = (): string | null => readCookie(SESSION_ID_KEY);

/**
 * Returns the active session_id, creating or rotating it when needed.
 * Rotation happens when no session cookie exists or the stored user differs
 * from the current one. Cookie Max-Age (30 min) handles idle expiry — when
 * the cookie expires, the next call sees no session and creates a fresh one.
 *
 * Returns null when user/company context is missing so callers can decide
 * whether to attach a session_id to events.
 */
export const ensureSession = (context: SessionContext): string | null => {
  if (!context.userId || !context.companyId) return null;

  const existingId = readCookie(SESSION_ID_KEY);
  const storedUserId = readCookie(SESSION_USER_ID_KEY);
  const shouldRotate = !existingId || storedUserId !== context.userId;

  let sessionId: string;
  if (shouldRotate) {
    sessionId = generateSessionId();
    writeCookie(SESSION_ID_KEY, sessionId);
    writeCookie(SESSION_USER_ID_KEY, context.userId);
    fireSessionStarted(sessionId, Date.now(), context);
  } else {
    sessionId = existingId as string;
    // Refresh both cookies so the sliding-window expiry resets.
    writeCookie(SESSION_ID_KEY, sessionId);
    writeCookie(SESSION_USER_ID_KEY, context.userId);
  }

  return sessionId;
};

const sessionMiddleware: MiddlewareFunction = ({ payload, next }) => {
  const sessionId = getSessionId();
  if (sessionId && payload?.obj?.type === 'track') {
    const properties = (payload.obj as { properties?: Record<string, unknown> })
      .properties;
    (payload.obj as { properties?: Record<string, unknown> }).properties = {
      ...(properties || {}),
      session_id: sessionId,
    };
  }
  next(payload);
};

/**
 * Anything that can take a source middleware. Deliberately not
 * `AnalyticsBrowser`: this function calls one method and ignores what it
 * returns, and asking for the whole class would exclude the inert stand-in
 * `segmentAnalytics` falls back to when there is no write key. Stating the real
 * requirement beats casting the stub, which would switch off type checking at
 * every other call site too.
 */
type SourceMiddlewareTarget = {
  addSourceMiddleware: (middleware: MiddlewareFunction) => unknown;
};

export const registerSessionMiddleware = (
  analyticsInstance: SourceMiddlewareTarget,
): void => {
  analyticsInstance.addSourceMiddleware(sessionMiddleware);
};
